import { useCallback, useEffect, useState } from "react";
import { ApiError } from "@/lib/api";

export interface Loaded<T> {
  data: T | null;
  error: string | null;
}

function messageOf(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return "Ismeretlen hiba.";
}

/** One read, its last answer and its last refusal, and a way to ask again.
 *
 * A failed refresh keeps the numbers it had and says that it failed, rather
 * than blanking a screen somebody is watching during a warmup. `deps` are
 * what the read depends on; a change runs it again. */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]): [Loaded<T>, () => void] {
  const [state, setState] = useState<Loaded<T>>({ data: null, error: null });
  const run = useCallback(() => {
    load()
      .then((data) => setState({ data, error: null }))
      .catch((cause: unknown) =>
        setState((previous) => ({ data: previous.data, error: messageOf(cause) })),
      );
    // The caller names what the load depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => run(), [run]);
  return [state, run];
}
